"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"
import { useOrganization } from "@clerk/nextjs"
import { toast } from "@coval/ui"
import { useQueryClient } from "@tanstack/react-query"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { DashboardMetadataResource } from "@/types_api/backend/transport/list_dashboard_metadata_response"
import { isDashboardRoute } from "@/utils/isDashboardRoute"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

import { dashboardDisplayName } from "../utils/dashboardName"
import {
  createDashboardDeletedPublisher,
  subscribeToDashboardDeleted,
} from "../widgets/utils/dashboardDeletedChannel"
import {
  serializeDeleteDashboardRequest,
  serializeGetDashboardLayoutRequest,
} from "../widgets/utils/transportDashboard"
import { isTransportDashboardQueryLoading } from "../widgets/utils/transportDashboardQueries"

const log = logger("DashboardShellState")

const toDashboardFetchError = (error: unknown): Error | string | undefined => {
  if (error instanceof Error || typeof error === "string") {
    return error
  }

  return undefined
}

const getDashboardIdFromResourceName = (resourceName: string): string => {
  const resourceSegments = resourceName.split("/")
  return resourceSegments[resourceSegments.length - 1] ?? resourceName
}

const mapDashboardMetadataResource = (
  dashboard: DashboardMetadataResource,
) => ({
  id: getDashboardIdFromResourceName(dashboard.name),
  display_name: dashboardDisplayName(dashboard),
  is_default: dashboard.is_default,
  is_favorite: dashboard.is_favorite,
})

interface DashboardMetadataListResponse {
  dashboards: DashboardMetadataResource[]
}

export const useDashboardShellState = () => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()
  const { trackEvent } = useAnalytics()
  const dashboardId = router.query.dashboardId as string | undefined
  const isDashboardPage = isDashboardRoute(router.pathname)
  const [currentDashboardId, setCurrentDashboardId] = useState<
    string | undefined
  >(dashboardId)
  const locallySelectedDashboardIdRef = useRef<string | null>(null)

  const organizationSlug = organization?.slug
  const previousOrganizationSlugRef = useRef<string | undefined>(
    organizationSlug,
  )

  const transportDashboardsQuery = useTransportQuery({
    operation: "listDashboardMetadata",
    options: {
      ...CACHE_PROFILES.lookup,
    },
  })
  const dashboards = useMemo(
    () =>
      transportDashboardsQuery.data?.dashboards.map(
        mapDashboardMetadataResource,
      ) ?? [],
    [transportDashboardsQuery.data?.dashboards],
  )
  const dashboardsLoading = isTransportDashboardQueryLoading(
    transportDashboardsQuery,
  )
  const dashboardsError = transportDashboardsQuery.error

  const createDashboard = useTransportMutation("createDashboard")
  const updateDashboard = useTransportMutation("updateDashboard")
  const deleteDashboard = useTransportMutation("deleteDashboard")

  const dashboardListQueryKey = useMemo(
    () => transportQueryUtils.getKey("listDashboardMetadata"),
    [transportQueryUtils],
  )

  const scopeIdentity = JSON.stringify(dashboardListQueryKey)
  const generationRef = useRef(0)
  const creatingRef = useRef<number | null>(null)
  const updatingRef = useRef<number | null>(null)
  const [isCreatingDashboard, setIsCreatingDashboard] = useState(false)
  const [isUpdatingDashboard, setIsUpdatingDashboard] = useState(false)
  useEffect(() => {
    generationRef.current += 1
    setIsCreatingDashboard(false)
    setIsUpdatingDashboard(false)
    return () => {
      generationRef.current += 1
    }
  }, [scopeIdentity])

  const refreshDashboardMetadata = useCallback(async () => {
    try {
      await transportQueryUtils.invalidateOperation("listDashboardMetadata")
    } catch (error) {
      log.error("Could not refresh dashboard metadata", error)
    }
  }, [transportQueryUtils])

  // Keeps a list open in another tab from offering a row that was deleted here.
  useEffect(
    () =>
      subscribeToDashboardDeleted(({ organizationSlug }) => {
        if (organizationSlug === getRouteOrganizationSlug(router.asPath)) {
          void refreshDashboardMetadata()
        }
      }),
    [refreshDashboardMetadata, router.asPath],
  )

  useEffect(() => {
    setCurrentDashboardId(dashboardId)
  }, [dashboardId])

  useEffect(() => {
    if (previousOrganizationSlugRef.current === organizationSlug) {
      return
    }

    previousOrganizationSlugRef.current = organizationSlug
    locallySelectedDashboardIdRef.current = null

    if (!dashboardId) {
      setCurrentDashboardId(undefined)
    }
  }, [dashboardId, organizationSlug])

  useEffect(() => {
    if (!isDashboardPage || dashboards.length === 0) {
      return
    }

    if (dashboardId) {
      locallySelectedDashboardIdRef.current = null
      return
    }

    const selectedDashboardExists =
      currentDashboardId != null &&
      dashboards.some((dashboard) => dashboard.id === currentDashboardId)

    if (selectedDashboardExists) {
      locallySelectedDashboardIdRef.current = null
      return
    }

    if (locallySelectedDashboardIdRef.current === currentDashboardId) {
      return
    }

    const defaultDashboard =
      dashboards.find((dashboard) => dashboard.is_default) || dashboards[0]
    setCurrentDashboardId(defaultDashboard.id)
  }, [currentDashboardId, dashboardId, dashboards, isDashboardPage])

  const currentDashboard = useMemo(
    () => dashboards.find((dashboard) => dashboard.id === currentDashboardId),
    [dashboards, currentDashboardId],
  )

  const handleCreateDashboard = useCallback(
    async (displayName: string) => {
      const generation = generationRef.current
      const response = await createDashboard.mutateAsync({
        display_name: displayName,
      })
      await refreshDashboardMetadata()
      if (generation !== generationRef.current) {
        return undefined
      }
      const createdDashboard = mapDashboardMetadataResource(response.dashboard)

      queryClient.setQueryData(
        dashboardListQueryKey,
        (existingData: DashboardMetadataListResponse | undefined) => {
          if (!existingData) {
            return existingData
          }

          return {
            dashboards: [
              ...existingData.dashboards.filter(
                (dashboard) => dashboard.name !== response.dashboard.name,
              ),
              response.dashboard,
            ],
          }
        },
      )
      locallySelectedDashboardIdRef.current = createdDashboard.id
      setCurrentDashboardId(createdDashboard.id)

      trackEvent(TRACKING_EVENTS.DASHBOARD.CREATED, {
        dashboard_id: createdDashboard.id,
        dashboard_name: displayName,
      })

      return createdDashboard
    },
    [
      createDashboard,
      dashboardListQueryKey,
      trackEvent,
      queryClient,
      refreshDashboardMetadata,
    ],
  )

  const handleToggleFavorite = useCallback(
    async (id: string) => {
      const dashboard = dashboards.find(
        (candidateDashboard) => candidateDashboard.id === id,
      )
      if (!dashboard || updatingRef.current === generationRef.current) {
        return
      }

      const generation = generationRef.current
      updatingRef.current = generation
      setIsUpdatingDashboard(true)
      await queryClient.cancelQueries({ queryKey: dashboardListQueryKey })
      if (generation !== generationRef.current) {
        if (updatingRef.current === generation) {
          updatingRef.current = null
        }
        return
      }
      queryClient.setQueryData(
        dashboardListQueryKey,
        (existingData: DashboardMetadataListResponse | undefined) => {
          if (!existingData) {
            return existingData
          }

          return {
            dashboards: existingData.dashboards.map((candidateDashboard) =>
              getDashboardIdFromResourceName(candidateDashboard.name) === id
                ? {
                    ...candidateDashboard,
                    is_favorite: !candidateDashboard.is_favorite,
                  }
                : candidateDashboard,
            ),
          }
        },
      )

      try {
        await updateDashboard.mutateAsync({
          dashboard_id: id,
          is_favorite: !dashboard.is_favorite,
        })
      } catch (error) {
        // Revert only this row so a dashboard deleted meanwhile stays gone.
        queryClient.setQueryData(
          dashboardListQueryKey,
          (existingData: DashboardMetadataListResponse | undefined) =>
            existingData && {
              dashboards: existingData.dashboards.map((candidateDashboard) =>
                getDashboardIdFromResourceName(candidateDashboard.name) === id
                  ? {
                      ...candidateDashboard,
                      is_favorite: dashboard.is_favorite,
                    }
                  : candidateDashboard,
              ),
            },
        )
        if (generation !== generationRef.current) {
          return
        }
        log.error("Failed to update dashboard favorite", error, {
          dashboardId: id,
        })
        toast.error({
          title: "Could not update favorite",
          error,
          fallback: "Could not update the dashboard favorite.",
        })
      } finally {
        await refreshDashboardMetadata()
        if (updatingRef.current === generation) {
          updatingRef.current = null
        }
        if (generation === generationRef.current) {
          setIsUpdatingDashboard(false)
        }
      }
    },
    [
      dashboardListQueryKey,
      dashboards,
      queryClient,
      updateDashboard,
      refreshDashboardMetadata,
    ],
  )

  const handleDeleteDashboard = useCallback(
    async (id: string): Promise<boolean> => {
      const generation = generationRef.current
      const publishDeleted = createDashboardDeletedPublisher()
      try {
        await deleteDashboard.mutateAsync(serializeDeleteDashboardRequest(id))
        // Broadcast even if this list has since unmounted: the delete happened.
        publishDeleted(id)
        if (generation !== generationRef.current) {
          return false
        }

        queryClient.removeQueries({
          queryKey: transportQueryUtils.getKey(
            "getDashboardLayout",
            serializeGetDashboardLayoutRequest(id),
          ),
        })
        await queryClient.cancelQueries({ queryKey: dashboardListQueryKey })
        if (generation !== generationRef.current) {
          return false
        }
        queryClient.setQueryData(
          dashboardListQueryKey,
          (existingData: DashboardMetadataListResponse | undefined) => {
            if (!existingData) {
              return existingData
            }

            return {
              dashboards: existingData.dashboards.filter(
                (candidateDashboard) =>
                  getDashboardIdFromResourceName(candidateDashboard.name) !==
                  id,
              ),
            }
          },
        )
        if (currentDashboardId === id) {
          setCurrentDashboardId(undefined)
        }

        toast.success({
          title: "Dashboard deleted",
          description: "The dashboard has been deleted.",
        })
        return true
      } catch (error) {
        if (generation !== generationRef.current) {
          return false
        }
        log.error("Failed to delete dashboard", error, { dashboardId: id })
        toast.danger({
          title: "Failed to delete dashboard",
          description: "The dashboard couldn't be deleted. Please try again.",
        })
        return false
      } finally {
        void refreshDashboardMetadata()
      }
    },
    [
      currentDashboardId,
      dashboardListQueryKey,
      deleteDashboard,
      queryClient,
      refreshDashboardMetadata,
      transportQueryUtils,
    ],
  )

  const handleNewDashboard = useCallback(
    async (displayName = "New Dashboard") => {
      if (creatingRef.current === generationRef.current) {
        return false
      }
      const generation = generationRef.current
      creatingRef.current = generation
      setIsCreatingDashboard(true)
      try {
        const res = await handleCreateDashboard(displayName)
        if (!res || generation !== generationRef.current) {
          return false
        }
        const navigationSlug =
          (router.query.slug as string | undefined) ?? organizationSlug
        if (res?.id && navigationSlug) {
          await router.push(
            queryParams.getHref(`/dashboard/${res.id}`, navigationSlug),
          )
        }
        return true
      } catch (error) {
        void refreshDashboardMetadata()
        if (generation !== generationRef.current) {
          return false
        }
        log.error("Failed to create dashboard", error, {
          organizationSlug,
        })
        toast.error({
          title: "Could not create dashboard",
          error,
          fallback: "Could not create the dashboard.",
        })
        return false
      } finally {
        if (creatingRef.current === generation) {
          creatingRef.current = null
        }
        if (generation === generationRef.current) {
          setIsCreatingDashboard(false)
        }
      }
    },
    [
      handleCreateDashboard,
      organizationSlug,
      queryParams,
      router,
      refreshDashboardMetadata,
    ],
  )

  return {
    currentDashboard,
    currentDashboardId,
    dashboardFetchError: toDashboardFetchError(dashboardsError),
    dashboards,
    dashboardsLoading,
    dashboardMetadata: transportDashboardsQuery.data?.dashboards,
    retryDashboards: transportDashboardsQuery.refetch,
    isCreatingDashboard,
    isUpdatingDashboard,
    handleDeleteDashboard,
    handleNewDashboard,
    handleToggleFavorite,
    organizationSlug,
  }
}
